import { useState, useEffect, useMemo } from "react";

import dayjs from "dayjs";

// RTK Query
import {
  useBulkConciliarMutation,
  useBulkReactivarMutation,
  useObtenerMovimientosQuery,
  useReactivarMovimientoMutation,
} from "../../services/conciliacionApi";
import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Cancel,
  CheckCircle,
  Clear,
  Edit,
  FilterList,
  FlashOn,
  MoreVert,
  Search,
  Undo,
  Visibility,
  MonetizationOn as MonetizationOnIcon,
} from "@material-ui/icons";
import { DatePicker } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { LocalizationProvider } from "@material-ui/lab";
import { es } from "date-fns/locale";
import {
  formatearMonto,
  formatearRutDisplay,
  getBancoConfig,
  getEstadoMovimientoConfig,
  getScoreConfig,
} from "src/utils/conciliacionHelpers";
import SearchNotFound from "../SearchNotFound";
import { TablaHead } from "../_dashboard/tablas";
import SugerenciasModal from "./SugerenciasModal";
import ConciliacionManualModal from "./ConciliacionManualModal";
import ModalProcesarAutomatico from "./ModalProcesarAutomatico";
import ModalResultadosProcesamiento from "./ModalResultadosProcesamiento";
import { useSnackbar } from "src/components/snackbar";
import DialogRechazarMovimientos from "./DialogRechazarMovimientos";
import ModalDetalleConciliacion from "./ModalDetalleConciliacion";
import { formatearFechaDisplay } from "src/utils/formatTime";
import DialogRevertirConciliacion from "./DialogRevertirConciliacion";
import DialogRevertirBulk from "./DialogRevertirBulk";

// ==================== CONFIGURACIÓN TABLA ====================

const TABLE_HEAD = [
  { id: "fecha_movimiento", label: "Fecha Mov", alignRight: false },
  { id: "banco", label: "Banco", alignRight: false },
  { id: "rut_ordenante", label: "RUT Ordenante", alignRight: false },
  { id: "nombre_ordenante", label: "Nombre Ordenante", alignRight: false },
  { id: "glosa", label: "Glosa", alignRight: false },
  { id: "monto", label: "Monto", alignRight: true },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "score", label: "Score", alignRight: false },
  { id: "acciones", label: "Acciones", alignRight: false },
];

// ==================== OPCIONES DE FILTROS ====================

const opcionesEstado = [
  { value: "", label: "Todos" },
  { value: "PENDIENTE", label: "Pendiente" },
  { value: "CONCILIADO", label: "Conciliado" },
  { value: "IGNORADO", label: "Ignorado" },
];

const opcionesBanco = [
  { value: "", label: "Todos" },
  { value: "SANTANDER", label: "Santander" },
  { value: "BCI", label: "BCI" },
];

export default function MovimientosTable({ loteId }) {
  const { enqueueSnackbar } = useSnackbar();

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtros
  const [filtros, setFiltros] = useState({
    estado: "",
    banco: "",
    loteId: loteId || "",
    fechaDesde: null,
    fechaHasta: null,
    rutOrdenante: "",
    montoMin: "",
    montoMax: "",
    busqueda: "",
  });

  // Selección múltiple (bulk operations)
  const [selected, setSelected] = useState([]);

  // Menú de acciones
  const [anchorEl, setAnchorEl] = useState(null);
  const [movimientoSeleccionado, setMovimientoSeleccionado] = useState(null);

  // Modales
  const [openSugerencias, setOpenSugerencias] = useState({
    open: false,
    movimiento: null,
  });
  const [openConciliar, setOpenConciliar] = useState({
    open: false,
    movimiento: null,
  });
  const [modalDetalle, setModalDetalle] = useState({
    open: false,
    conciliacion: null,
  });
  const [dialogRevertir, setDialogRevertir] = useState({
    open: false,
    conciliacion: null,
  });
  const [dialogRevertirBulk, setDialogRevertirBulk] = useState({
    open: false,
    movimientos: [],
  });

  const [modalProcesar, setModalProcesar] = useState({
    open: false,
    movimientoIds: [],
  });
  const [modalResultados, setModalResultados] = useState({
    open: false,
    resultados: null,
  });

  // ==================== RTK QUERY ====================

  const { data, isLoading, isFetching } = useObtenerMovimientosQuery({
    estado: filtros.estado || undefined,
    banco: filtros.banco || undefined,
    loteId: filtros.loteId || undefined,
    fechaDesde: filtros.fechaDesde
      ? dayjs(filtros.fechaDesde).format("YYYY-MM-DD")
      : undefined,
    fechaHasta: filtros.fechaHasta
      ? dayjs(filtros.fechaHasta).format("YYYY-MM-DD")
      : undefined,
    rutOrdenante: filtros.rutOrdenante || undefined,
    montoMin: filtros.montoMin || undefined,
    montoMax: filtros.montoMax || undefined,
    busqueda: filtros.busqueda || undefined,
    page: page + 1,
    limit: rowsPerPage,
  });

  const movimientos = useMemo(() => {
    return data?.data || [];
  }, [data]);
  const totalMovimientos = data?.pagination?.total || 0;

  // ==================== MUTATIONS ====================|

  const [bulkConciliar, { isLoading: isConciliandoBulk }] =
    useBulkConciliarMutation();

  const [reactivarMovimiento, { isLoading: isReactivando }] =
    useReactivarMovimientoMutation();
  const [reactivarBulk, { isLoading: isReactivandoBulk }] =
    useBulkReactivarMutation();

  // Estado para diálogo de rechazo
  const [dialogRechazo, setDialogRechazo] = useState({
    open: false,
    movimientos: [],
  });

  // ==================== HELPERS ====================

  const contarFiltrosActivos = () => {
    let count = 0;
    if (filtros.estado) count++;
    if (filtros.banco) count++;
    if (filtros.fechaDesde) count++;
    if (filtros.fechaHasta) count++;
    if (filtros.rutOrdenante) count++;
    if (filtros.montoMin) count++;
    if (filtros.montoMax) count++;
    if (filtros.busqueda) count++;
    return count;
  };

  const filtrosActivos = contarFiltrosActivos();

  const truncarTexto = (texto, maxLength = 50) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  // Obtener movimientos seleccionados
  const movimientosSeleccionados = useMemo(() => {
    return movimientos.filter((mov) => selected.includes(mov.id));
  }, [movimientos, selected]);

  // Validar si todos son PENDIENTES
  const todosPendientes = useMemo(() => {
    return movimientosSeleccionados.every((mov) => mov.estado === "PENDIENTE");
  }, [movimientosSeleccionados]);

  // Validar si todos son CONCILIADOS
  const todosConciliados = useMemo(() => {
    return movimientosSeleccionados.every((mov) => mov.estado === "CONCILIADO");
  }, [movimientosSeleccionados]);

  // Validar si todos son IGNORADOS
  const todosIgnorados = useMemo(() => {
    return movimientosSeleccionados.every((mov) => mov.estado === "IGNORADO");
  }, [movimientosSeleccionados]);

  // Calcular cuántos pendientes están seleccionados
  const selectedPendientes = useMemo(() => {
    return movimientos
      .filter((mov) => selected.includes(mov.id) && mov.estado === "PENDIENTE")
      .map((mov) => mov.id);
  }, [selected, movimientos]);

  // Validar límites
  const puedeConcilarBulk = todosPendientes && selected.length <= 50;
  const puedeRechazarBulk = todosPendientes && selected.length <= 100;
  const puedeRevertirBulk = useMemo(() => {
    if (selected.length === 0 || selected.length > 100) return false;

    // Contar movimientos conciliados con conciliación válida
    const conciliadosValidos = movimientosSeleccionados.filter(
      (mov) =>
        mov.estado === "CONCILIADO" &&
        mov.conciliacion?.id &&
        mov.conciliacion.estado !== "REVERTIDO",
    );

    return conciliadosValidos.length > 0;
  }, [movimientosSeleccionados, selected.length]);
  const puedeReactivarBulk = todosIgnorados && selected.length <= 100;

  // ==================== EFECTOS ====================

  // Si viene loteId desde props, actualizar filtros
  useEffect(() => {
    if (loteId) {
      setFiltros((prev) => ({ ...prev, loteId }));
      setPage(0); // Reset paginación
    }
  }, [loteId]);

  // ==================== HANDLERS ====================

  // Paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPage(0); // Reset paginación al cambiar filtros
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      estado: "",
      banco: "",
      loteId: loteId || "",
      fechaDesde: null,
      fechaHasta: null,
      rutOrdenante: "",
      montoMin: "",
      montoMax: "",
      busqueda: "",
    });
    setPage(0);
  };

  // Menú de acciones
  const handleOpenMenu = (event, movimiento) => {
    setAnchorEl(event.currentTarget);
    setMovimientoSeleccionado(movimiento);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMovimientoSeleccionado(null);
  };

  // Selección múltiple
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = movimientos.map((mov) => mov.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  // ==================== HANDLERS DE ACCIONES ====================

  // Ver Sugerencias
  const handleVerSugerencias = (movimiento) => {
    setOpenSugerencias({ open: true, movimiento: movimiento });
    handleCloseMenu();
  };

  // Conciliar Manual
  const handleConciliarManual = (movimiento) => {
    setMovimientoSeleccionado(movimiento);
    setOpenConciliar({ open: true, movimiento: movimiento });
    handleCloseMenu();
  };

  // Ver Detalle de Conciliación
  const handleVerDetalle = (movimiento) => {
    setModalDetalle({
      open: true,
      conciliacion: movimiento.conciliacion,
    });
    handleCloseMenu();
  };

  const handleCerrarDetalle = () => {
    setModalDetalle({
      open: false,
      conciliacion: null,
    });
  };

  // Rechazar Movimiento
  const handleRechazar = (movimiento) => {
    setDialogRechazo({
      open: true,
      movimientos: [movimiento],
    });
    handleCloseMenu();
  };

  // Revertir Conciliación
  const handleRevertir = (movimiento) => {
    // Validación defensiva
    if (!movimiento?.conciliacion?.id) {
      enqueueSnackbar("Este movimiento no tiene una conciliación asociada", {
        variant: "warning",
      });
      handleCloseMenu();
      return;
    }

    // Validar que no esté ya revertida
    if (movimiento.conciliacion.estado === "REVERTIDO") {
      enqueueSnackbar("Esta conciliación ya fue revertida anteriormente", {
        variant: "info",
      });
      handleCloseMenu();
      return;
    }

    // Abrir modal de confirmación con los datos de la conciliación
    setDialogRevertir({
      open: true,
      conciliacion: movimiento.conciliacion,
    });

    handleCloseMenu();
  };

  // Cerrar Dialog de Reversión
  const handleCerrarDialogRevertir = () => {
    setDialogRevertir({
      open: false,
      conciliacion: null,
    });
  };

  // Deshacer Rechazo
  const handleDeshacerRechazo = async (movimiento) => {
    try {
      await reactivarMovimiento({
        movimiento_id: movimiento.id,
        motivo: "Reactivado desde interfaz",
      }).unwrap();

      enqueueSnackbar("Movimiento reactivado exitosamente", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error al reactivar movimiento:", error);
      enqueueSnackbar(error?.data?.message || "Error al reactivar movimiento", {
        variant: "error",
      });
    }
    handleCloseMenu();
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // ==================== HANDLERS BULK ====================

  // Conciliar múltiples (automático)
  const handleBulkConciliar = async () => {
    if (!puedeConcilarBulk) {
      console.error("No se puede conciliar: validación fallida");
      return;
    }

    try {
      await bulkConciliar({
        movimientos: selected,
      }).unwrap();

      setSelected([]);
    } catch (error) {
      console.error("Error en conciliación masiva:", error);
    }
  };

  // Rechazar múltiples
  const handleBulkRechazar = () => {
    if (!puedeRechazarBulk) {
      enqueueSnackbar("Selecciona solo movimientos PENDIENTES (máx 100)", {
        variant: "warning",
      });
      return;
    }

    setDialogRechazo({
      open: true,
      movimientos: movimientosSeleccionados,
    });
  };

  // Revertir múltiples
  const handleBulkRevertir = () => {
    // Validación: debe haber selección
    if (selected.length === 0) {
      enqueueSnackbar("Debe seleccionar al menos un movimiento", {
        variant: "warning",
      });
      return;
    }

    // Filtrar solo movimientos CONCILIADOS con conciliación válida
    const movimientosConciliados = movimientosSeleccionados.filter(
      (mov) =>
        mov.estado === "CONCILIADO" &&
        mov.conciliacion?.id &&
        mov.conciliacion.estado !== "REVERTIDO",
    );

    // Validar que haya al menos uno válido
    if (movimientosConciliados.length === 0) {
      enqueueSnackbar(
        "Los movimientos seleccionados no tienen conciliaciones válidas para revertir",
        { variant: "warning" },
      );
      return;
    }

    // Validar límite de 100
    if (movimientosConciliados.length > 100) {
      enqueueSnackbar("Máximo 100 conciliaciones por operación masiva", {
        variant: "warning",
      });
      return;
    }

    // Abrir modal de confirmación con los movimientos válidos
    setDialogRevertirBulk({
      open: true,
      movimientos: movimientosConciliados,
    });
  };

  // Cerrar Dialog de Reversión Bulk
  const handleCerrarDialogRevertirBulk = (resultado) => {
    // Si hay resultado, limpiar selección
    if (resultado) {
      setSelected([]);
    }

    setDialogRevertirBulk({
      open: false,
      movimientos: [],
    });
  };

  // Reactivar múltiples
  const handleBulkReactivar = async () => {
    if (!puedeReactivarBulk) {
      enqueueSnackbar("Selecciona solo movimientos IGNORADOS (máx 100)", {
        variant: "warning",
      });
      return;
    }

    try {
      const resultado = await reactivarBulk({
        movimiento_ids: selected,
        motivo: "Reactivación masiva desde interfaz",
      }).unwrap();

      const { exitosos, fallidos } = resultado.resultados;

      if (fallidos.length > 0) {
        enqueueSnackbar(
          `${exitosos.length} reactivados, ${fallidos.length} fallaron`,
          { variant: "warning" },
        );
      } else {
        enqueueSnackbar(
          `${exitosos.length} movimientos reactivados exitosamente`,
          { variant: "success" },
        );
      }

      setSelected([]);
    } catch (error) {
      console.error("Error en reactivación masiva:", error);
      enqueueSnackbar(
        error?.data?.message || "Error al reactivar movimientos",
        { variant: "error" },
      );
    }
  };

  const handleProcesarSeleccionados = () => {
    // Filtrar solo movimientos PENDIENTES
    const movimientosPendientes = movimientos
      .filter((mov) => selected.includes(mov.id) && mov.estado === "PENDIENTE")
      .map((mov) => mov.id);

    if (movimientosPendientes.length === 0) {
      enqueueSnackbar("No hay movimientos pendientes seleccionados", {
        variant: "warning",
      });
      return;
    }

    setModalProcesar({
      open: true,
      movimientoIds: movimientosPendientes,
    });
  };

  const handleSuccessProcesamiento = (resultados) => {
    // Cerrar modal de confirmación
    setModalProcesar({ open: false, movimientoIds: [] });

    // Limpiar selección
    setSelected([]);

    // Abrir modal de resultados
    setModalResultados({
      open: true,
      resultados: resultados,
    });
  };

  // ==================== RENDER ====================

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
      <Card>
        <Box sx={{ p: 2 }}>
          {/* ==================== FILTROS ==================== */}
          <Box sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <FilterList />
              <Typography variant="h6">Filtros</Typography>
              {filtrosActivos > 0 && (
                <Chip
                  label={`${filtrosActivos} filtro(s) activo(s)`}
                  size="small"
                  color="primary"
                />
              )}
            </Stack>

            <Grid container spacing={2}>
              {/* Fila 1: Estado, Banco, Fecha Desde, Fecha Hasta */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtros.estado}
                    label="Estado"
                    onChange={(e) =>
                      handleFiltroChange("estado", e.target.value)
                    }
                  >
                    {opcionesEstado.map((opcion) => (
                      <MenuItem key={opcion.value} value={opcion.value}>
                        {opcion.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Banco</InputLabel>
                  <Select
                    value={filtros.banco}
                    label="Banco"
                    onChange={(e) =>
                      handleFiltroChange("banco", e.target.value)
                    }
                  >
                    {opcionesBanco.map((opcion) => (
                      <MenuItem key={opcion.value} value={opcion.value}>
                        {opcion.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Desde"
                  value={filtros.fechaDesde}
                  onChange={(newValue) =>
                    handleFiltroChange("fechaDesde", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Hasta"
                  value={filtros.fechaHasta}
                  onChange={(newValue) =>
                    handleFiltroChange("fechaHasta", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
              </Grid>

              {/* Fila 2: RUT, Monto Min, Monto Max, Búsqueda */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="RUT Ordenante"
                  placeholder="12345678-9"
                  value={filtros.rutOrdenante}
                  onChange={(e) =>
                    handleFiltroChange("rutOrdenante", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monto Mínimo"
                  type="number"
                  placeholder="0"
                  value={filtros.montoMin}
                  onChange={(e) =>
                    handleFiltroChange("montoMin", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <Typography variant="body2" sx={{ mr: 0.5 }}>
                        $
                      </Typography>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monto Máximo"
                  type="number"
                  placeholder="0"
                  value={filtros.montoMax}
                  onChange={(e) =>
                    handleFiltroChange("montoMax", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <Typography variant="body2" sx={{ mr: 0.5 }}>
                        $
                      </Typography>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar en Glosa/Nombre"
                  placeholder="Buscar..."
                  value={filtros.busqueda}
                  onChange={(e) =>
                    handleFiltroChange("busqueda", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <Search sx={{ mr: 0.5, color: "text.disabled" }} />
                    ),
                  }}
                />
              </Grid>

              {/* Botón Limpiar Filtros */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleLimpiarFiltros}
                  disabled={filtrosActivos === 0}
                >
                  Limpiar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* ==================== TOOLBAR BULK ACTIONS ==================== */}
        {selected.length > 0 && (
          <Toolbar
            sx={{
              bgcolor: "primary.lighter",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {selected.length} movimiento(s) seleccionado(s)
                {todosPendientes && (
                  <Chip
                    label="Pendientes"
                    size="small"
                    color="info"
                    sx={{ ml: 1 }}
                  />
                )}
                {todosConciliados && (
                  <Chip
                    label="Conciliados"
                    size="small"
                    color="success"
                    sx={{ ml: 1 }}
                  />
                )}
                {todosIgnorados && (
                  <Chip
                    label="Ignorados"
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
                {!todosPendientes && !todosConciliados && !todosIgnorados && (
                  <Chip
                    label="Estados mixtos"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              {selectedPendientes.length > 0 && (
                <Tooltip title="Procesar movimientos seleccionados automáticamente">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<FlashOn />}
                    onClick={handleProcesarSeleccionados}
                    disabled
                  >
                    Procesar ({selectedPendientes.length})
                  </Button>
                </Tooltip>
              )}

              {/* Conciliar Automático */}
              <Tooltip
                title={
                  !puedeConcilarBulk
                    ? !todosPendientes
                      ? "Solo movimientos PENDIENTES"
                      : "Máximo 50 movimientos"
                    : "Conciliar automáticamente"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircle />}
                    onClick={handleBulkConciliar}
                    // disabled={!puedeConcilarBulk || isConciliandoBulk}
                    disabled
                  >
                    {isConciliandoBulk ? "Conciliando..." : "Conciliar"}
                  </Button>
                </span>
              </Tooltip>

              {/* Rechazar */}
              <Tooltip
                title={
                  !puedeRechazarBulk
                    ? !todosPendientes
                      ? "Solo movimientos PENDIENTES"
                      : "Máximo 100 movimientos"
                    : "Rechazar movimientos"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<Cancel />}
                    onClick={handleBulkRechazar}
                    // disabled={!puedeRechazarBulk}
                    disabled
                  >
                    {isReactivando ? "Rechazando..." : "Rechazar"}
                  </Button>
                </span>
              </Tooltip>

              {/* Revertir */}
              <Tooltip
                title={
                  !puedeRevertirBulk
                    ? selected.length === 0
                      ? "Seleccione movimientos para revertir"
                      : selected.length > 100
                        ? "Máximo 100 movimientos"
                        : "Solo movimientos CONCILIADOS con conciliaciones válidas"
                    : "Revertir conciliaciones seleccionadas"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<Undo />}
                    onClick={handleBulkRevertir}
                    disabled={!puedeRevertirBulk}
                  >
                    Revertir
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  !puedeReactivarBulk
                    ? !todosIgnorados
                      ? "Solo movimientos IGNORADOS"
                      : "Máximo 100 movimientos"
                    : "Reactivar movimientos rechazados"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    startIcon={<Undo />}
                    onClick={handleBulkReactivar}
                    // disabled={!puedeReactivarBulk || isReactivandoBulk}
                    disabled
                  >
                    {isReactivandoBulk ? "Reactivando..." : "Deshacer Rechazo"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Toolbar>
        )}

        {/* TABLA */}
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TablaHead
              headLabel={TABLE_HEAD}
              rowCount={movimientos.length}
              selectedRows={selected}
              handleSelectAllClick={handleSelectAllClick}
              select={true}
            />
            <TableBody>
              {/* ==================== LOADING STATE ==================== */}
              {(isLoading || isFetching) && (
                <>
                  {[...Array(rowsPerPage)].map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell padding="checkbox">
                        <Skeleton
                          variant="rectangular"
                          width={18}
                          height={18}
                        />
                      </TableCell>
                      {TABLE_HEAD.map((head) => (
                        <TableCell
                          key={head.id}
                          align={head.alignRight ? "right" : "left"}
                        >
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              )}

              {/* ==================== DATOS REALES ==================== */}
              {!isLoading && !isFetching && movimientos.length > 0 && (
                <>
                  {movimientos.map((movimiento) => {
                    const isItemSelected = isSelected(movimiento.id);
                    const estadoConfig = getEstadoMovimientoConfig(
                      movimiento.estado,
                    );
                    const bancoConfig = getBancoConfig(movimiento.banco);
                    const scoreConfig = movimiento.conciliacion?.score
                      ? getScoreConfig(movimiento.conciliacion?.score)
                      : null;

                    return (
                      <TableRow
                        key={movimiento.id}
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        selected={isItemSelected}
                        tabIndex={-1}
                      >
                        {/* CHECKBOX */}
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleSelectClick(movimiento.id)}
                            color="primary"
                          />
                        </TableCell>

                        {/* FECHA MOVIMIENTO */}
                        <TableCell>
                          {formatearFechaDisplay(movimiento.fecha_mov)}
                        </TableCell>

                        {/* BANCO */}
                        <TableCell>
                          <Chip
                            label={bancoConfig.label}
                            size="small"
                            sx={{
                              bgcolor: bancoConfig.color,
                              color: "#fff",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>

                        {/* RUT ORDENANTE */}
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {movimiento.rut_ordenante
                              ? formatearRutDisplay(movimiento.rut_ordenante)
                              : "-"}
                          </Typography>
                        </TableCell>

                        {/* NOMBRE ORDENANTE */}
                        <TableCell>
                          <Tooltip
                            title={movimiento.nombre_ordenante || ""}
                            arrow
                          >
                            <Typography variant="body2" noWrap>
                              {truncarTexto(movimiento.nombre_ordenante, 30)}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* GLOSA */}
                        <TableCell>
                          <Tooltip title={movimiento.glosa || ""} arrow>
                            <Typography variant="body2" noWrap>
                              {truncarTexto(movimiento.glosa, 40)}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* MONTO */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color:
                                movimiento.monto >= 0
                                  ? "success.main"
                                  : "error.main",
                            }}
                          >
                            {formatearMonto(movimiento.monto)}
                          </Typography>
                        </TableCell>

                        {/* ESTADO */}
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {/* Chip de Estado Principal */}
                            <Chip
                              label={estadoConfig.label}
                              icon={estadoConfig.icon}
                              color={estadoConfig.color}
                              size="small"
                              sx={{
                                bgcolor: estadoConfig.color,
                                color: estadoConfig.textColor || "#fff",
                                fontWeight: 500,
                              }}
                            />

                            {/* Badge de Abono Pendiente */}
                            {movimiento.estado === "CONCILIADO" &&
                              movimiento.conciliacion?.tiene_abono_pendiente ===
                                1 &&
                              movimiento.conciliacion?.abono_aplicado === 0 && (
                                <Tooltip
                                  title={`Abono pendiente: ${formatearMonto(
                                    movimiento.conciliacion
                                      .monto_abono_pendiente,
                                  )}`}
                                  arrow
                                >
                                  <Chip
                                    icon={
                                      <MonetizationOnIcon
                                        style={{ fontSize: 12, color: "#fff" }}
                                      />
                                    }
                                    label={`Abono: ${formatearMonto(
                                      movimiento.conciliacion
                                        .monto_abono_pendiente,
                                    )}`}
                                    size="small"
                                    style={{
                                      backgroundColor: "#FF9800",
                                      color: "#fff",
                                      fontWeight: 600,
                                      fontSize: 10,
                                      height: 22,
                                    }}
                                  />
                                </Tooltip>
                              )}
                          </Box>
                        </TableCell>

                        {/* SCORE */}
                        <TableCell>
                          {scoreConfig ? (
                            <Tooltip title={scoreConfig.description} arrow>
                              <Badge
                                badgeContent={scoreConfig.score}
                                color={scoreConfig.color}
                                max={100}
                              >
                                <Chip
                                  label={scoreConfig.label}
                                  size="small"
                                  sx={{
                                    bgcolor: scoreConfig.badgeColor,
                                    color: "#fff",
                                    fontWeight: 500,
                                  }}
                                />
                              </Badge>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>

                        {/* ACCIONES */}
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, movimiento)}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </>
              )}

              {/* ==================== EMPTY STATE ==================== */}
              {!isLoading && !isFetching && movimientos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <SearchNotFound
                      searchQuery={filtros.busqueda || "movimientos"}
                      message="No se encontraron movimientos con los filtros aplicados"
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINACIÓN */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalMovimientos}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />

        {/* ==================== MENÚ DE ACCIONES ==================== */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* ===== PENDIENTE ===== */}
          {movimientoSeleccionado?.estado === "PENDIENTE" && (
            <MenuItem
              onClick={() => handleVerSugerencias(movimientoSeleccionado)}
            >
              <Search sx={{ mr: 1, fontSize: 20 }} />
              Ver Sugerencias
            </MenuItem>
          )}
          {movimientoSeleccionado?.estado === "PENDIENTE" && (
            <MenuItem
              onClick={() => handleConciliarManual(movimientoSeleccionado)}
            >
              <Edit sx={{ mr: 1, fontSize: 20 }} />
              Conciliar Manual
            </MenuItem>
          )}
          {movimientoSeleccionado?.estado === "PENDIENTE" && (
            <MenuItem
              onClick={() => handleRechazar(movimientoSeleccionado)}
              sx={{ color: "error.main" }}
            >
              <Cancel sx={{ mr: 1, fontSize: 20 }} />
              Rechazar
            </MenuItem>
          )}

          {/* ===== CONCILIADO ===== */}
          {movimientoSeleccionado?.estado === "CONCILIADO" && (
            <>
              <MenuItem
                onClick={() => handleVerDetalle(movimientoSeleccionado)}
              >
                <Visibility sx={{ mr: 1, fontSize: 20 }} />
                Ver Detalle
              </MenuItem>
              <MenuItem
                onClick={() => handleRevertir(movimientoSeleccionado)}
                sx={{ color: "warning.main" }}
                disabled={!movimientoSeleccionado?.conciliacion?.id}
              >
                <Undo sx={{ mr: 1, fontSize: 20 }} />
                Revertir Conciliación
              </MenuItem>
            </>
          )}

          {/* ===== IGNORADO ===== */}
          {movimientoSeleccionado?.estado === "IGNORADO" && (
            <MenuItem
              onClick={() => handleDeshacerRechazo(movimientoSeleccionado)}
              sx={{ color: "info.main" }}
            >
              <Undo sx={{ mr: 1, fontSize: 20 }} />
              Deshacer Rechazo
            </MenuItem>
          )}
        </Menu>

        <SugerenciasModal
          open={openSugerencias.open}
          onClose={() => setOpenSugerencias({ open: false, movimiento: null })}
          movimiento={openSugerencias.movimiento}
          onConciliarManual={(mov) => {
            setOpenSugerencias({ open: false, movimiento: null });
            setOpenConciliar({ open: true, movimiento: mov });
          }}
        />

        <ConciliacionManualModal
          open={openConciliar.open}
          onClose={() => setOpenConciliar({ open: false, movimiento: null })}
          movimiento={openConciliar.movimiento}
          onSuccess={() => {
            setOpenConciliar({ open: false, movimiento: null });
          }}
        />

        <ModalProcesarAutomatico
          open={modalProcesar.open}
          onClose={() => setModalProcesar({ open: false, movimientoIds: [] })}
          movimientoIds={modalProcesar.movimientoIds}
          onSuccess={handleSuccessProcesamiento}
        />

        <ModalResultadosProcesamiento
          open={modalResultados.open}
          onClose={() => setModalResultados({ open: false, resultados: null })}
          resultados={modalResultados.resultados}
        />

        {/* ==================== DIALOG DE RECHAZO ==================== */}
        {dialogRechazo.open && (
          <DialogRechazarMovimientos
            open={dialogRechazo.open}
            onClose={() => {
              setDialogRechazo({ open: false, movimientos: [] });
              setSelected([]);
            }}
            movimientos={dialogRechazo.movimientos}
          />
        )}

        {modalDetalle.open && (
          <ModalDetalleConciliacion
            open={modalDetalle.open}
            onClose={handleCerrarDetalle}
            conciliacion={modalDetalle.conciliacion}
          />
        )}
        {dialogRevertir.open && (
          <DialogRevertirConciliacion
            open={dialogRevertir.open}
            onClose={handleCerrarDialogRevertir}
            conciliacion={dialogRevertir.conciliacion}
          />
        )}
        {dialogRevertirBulk.open && (
          <DialogRevertirBulk
            open={dialogRevertirBulk.open}
            onClose={handleCerrarDialogRevertirBulk}
            movimientos={dialogRevertirBulk.movimientos}
          />
        )}
      </Card>
    </LocalizationProvider>
  );
}
